import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useRecordInvoicePayment } from "@/hooks/useInvoices";
import type { InvoiceWithLines, RecordPaymentPayload } from "@/interfaces/invoices";
import { RECEIPT_METHOD_OPTIONS } from "@/interfaces/invoices";

const money = (v: string | number | null | undefined, ccy = "usd") =>
  v == null
    ? "—"
    : `${ccy.toUpperCase()} ${Number(v).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

/** Number → comma-grouped, 2-dp string for the amount field ("1234.5" → "1,234.50"). */
const fmtAmount = (n: number) =>
  n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** "2026-07-21T14:30" for a datetime-local input, from now. */
function localNow(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Right-side drawer to record a payment received against a finalized invoice — full, partial, or with
 * the remaining balance written off. Amount pre-fills to the outstanding balance; reducing it makes a
 * partial. The write-off toggle forgives the gap (client still mints full; we book the write-off).
 */
export default function RecordPaymentDrawer({
  groupId,
  inv,
  open,
  onClose,
}: {
  groupId: number;
  inv: InvoiceWithLines;
  open: boolean;
  onClose: () => void;
}) {
  const record = useRecordInvoicePayment(groupId, inv.id);
  const remaining = Number(inv.netRemaining ?? 0);
  const [amount, setAmount] = useState(fmtAmount(remaining));
  const [method, setMethod] = useState<RecordPaymentPayload["method"]>(
    (["ach", "wire", "card", "check"][
      inv.expectedPaymentMethod
    ] as RecordPaymentPayload["method"]) ?? "ach",
  );
  const [receivedAt, setReceivedAt] = useState(localNow());
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [writeOff, setWriteOff] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const amt = Number(amount.replace(/,/g, "")) || 0;
  const writeOffAmt = writeOff ? Math.max(0, remaining - amt) : 0;
  const overpay = amt > remaining + 0.005;

  function submit() {
    setError(null);
    record.mutate(
      {
        amount: amt,
        method,
        receivedAt: new Date(receivedAt).toISOString(),
        reference: reference.trim() || null,
        note: note.trim() || null,
        writeOffRemainder: writeOff,
      },
      {
        onSuccess: () => {
          onClose();
          setReference("");
          setNote("");
          setWriteOff(false);
        },
        onError: (e) => setError(e instanceof Error ? e.message : "Couldn't record the payment."),
      },
    );
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: "100vw", sm: 440 }, maxWidth: "100vw", p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Record payment</Typography>
          <IconButton onClick={onClose} aria-label="Close">
            <Close />
          </IconButton>
        </Box>

        <Box
          sx={{
            border: "1px solid var(--color-smoke)",
            borderRadius: "10px",
            p: 2,
            mb: 2.5,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 12, color: "var(--color-graphite)" }}>
              {inv.invoiceNumber ?? `Invoice #${inv.id}`}
            </Typography>
            <Typography sx={{ fontSize: 13 }}>
              Total {money(inv.totalAmount, inv.currency)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography
              sx={{ fontSize: 11, textTransform: "uppercase", color: "var(--color-tan, #a98d6b)" }}
            >
              Outstanding
            </Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 500 }}>
              {money(remaining, inv.currency)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Amount received"
            type="text"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            onBlur={() => {
              const n = Number(amount.replace(/,/g, ""));
              if (Number.isFinite(n) && n >= 0) setAmount(fmtAmount(n));
            }}
            size="small"
            error={overpay}
            helperText={overpay ? "Can't exceed the outstanding balance" : undefined}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              },
              htmlInput: { inputMode: "decimal" },
            }}
          />
          <TextField
            select
            label="Method"
            value={method}
            onChange={(e) => setMethod(e.target.value as RecordPaymentPayload["method"])}
            size="small"
          >
            {RECEIPT_METHOD_OPTIONS.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Payment date & time"
            type="datetime-local"
            value={receivedAt}
            onChange={(e) => setReceivedAt(e.target.value)}
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label="Reference (wire/ACH memo)"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            size="small"
          />
          <TextField
            label="Internal note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            size="small"
            multiline
            minRows={2}
            placeholder="e.g. Paid via ACH on 5/1"
          />

          <FormControlLabel
            control={<Switch checked={writeOff} onChange={(e) => setWriteOff(e.target.checked)} />}
            label="Write off remaining balance"
          />
          {writeOff && writeOffAmt > 0 && (
            <Typography sx={{ fontSize: 12.5, color: "var(--color-graphite)", mt: -1 }}>
              {money(writeOffAmt, inv.currency)} will be written off — the invoice settles in full
              and mints all credits; the forgiven amount is booked separately.
            </Typography>
          )}

          {error && (
            <Typography sx={{ color: "var(--color-red, #c00)", fontSize: 13 }}>{error}</Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 1, mt: 3 }}>
          <Button variant="contained" onClick={submit} disabled={record.isPending || overpay}>
            {record.isPending ? "Recording…" : "Record payment"}
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </Box>
      </Box>
    </Drawer>
  );
}
